import { Tabs } from "antd";
import type { CSSProperties, FC } from "react";
import { useMemo } from "react";

import { canAccess } from "../../../../../core/router/auth/permissions";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import type { DiffOperation } from "./formatDiff";
import { formatDiff } from "./formatDiff";

// Indent is 4 spaces
const INDENT = "    ";

const preStyle: CSSProperties = {
    margin: 0,
    fontFamily: "monospace",
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
};

const segmentStyles: Record<DiffOperation, CSSProperties | undefined> = {
    equal: undefined,
    insert: { background: "#e6ffe6", textDecoration: "none" },
    delete: { background: "#ffe6e6" },
};

const segmentTags: Record<DiffOperation, "span" | "ins" | "del"> = {
    equal: "span",
    insert: "ins",
    delete: "del",
};

interface DiffFileProps {
    fileName: string;
    diffValue: string;
}

const DiffFile: FC<DiffFileProps> = ({ fileName, diffValue }) => {
    const lines = useMemo(() => formatDiff(fileName, diffValue), [fileName, diffValue]);

    return (
        <pre style={preStyle}>
            {lines.map(({ indent, segments }, lineIndex) => (
                <div key={lineIndex}>
                    {INDENT.repeat(indent)}
                    {segments.map(({ operation, text }, segmentIndex) => {
                        const Tag = segmentTags[operation];
                        return (
                            <Tag key={segmentIndex} style={segmentStyles[operation]}>
                                {text || (operation === "equal" ? "" : " ")}
                            </Tag>
                        );
                    })}
                    {segments.length === 0 && <br />}
                </div>
            ))}
        </pre>
    );
};

export interface DiffComponentProps {
    value?: Record<string, string>;
}

export const DiffComponent: FC<DiffComponentProps> = ({ value }) => {
    return (
        <Tabs>
            {value &&
                Object.entries(value)
                    .filter(([key]) => canAccess(ResourceName.TERMINAL_VERSIONS, key))
                    .map(([key, diffValue]) => (
                        <Tabs.TabPane tab={key} key={key}>
                            <DiffFile fileName={key} diffValue={diffValue} />
                        </Tabs.TabPane>
                    ))}
        </Tabs>
    );
};
